import { createSlice } from '@reduxjs/toolkit';
import { baseApi } from '../api/baseApi';

const initialState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: false,
  isInitialized: false,
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (state, action) => {
      const { user, token } = action.payload;
      console.log("setCredentials", user);

      state.user = user;
      state.token = token;
      state.isAuthenticated = true;
      state.error = null;

      // Store in localStorage. The refresh token is NEVER persisted client-side
      // \u2014 it lives only in the HttpOnly cookie set by the BE.
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
    },
    
    clearCredentials: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.isLoading = false;
      state.error = null;
      
      // Clear localStorage
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
    },
    
    // Enhanced logout that clears all cache
    logoutWithCacheClear: (state, action) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.isLoading = false;
      state.error = null;
      
      // Clear localStorage
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      
      // Clear RTK Query cache - this will be handled by extra reducer
    },
    
    // Force clear all auth data (for debugging)
    forceLogout: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.isLoading = false;
      state.error = null;
      
      // Clear all possible localStorage keys
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      localStorage.removeItem('authToken');
      localStorage.removeItem('currentUser');
      localStorage.clear(); // Clear everything if needed
    },
    
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    
    setError: (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    },
    
    updateUser: (state, action) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
        localStorage.setItem('user', JSON.stringify(state.user));
      }
    },
    
    initializeAuth: (state) => {
      // Set loading state
      state.isLoading = true;
      
      // Initialize from localStorage
      const token = localStorage.getItem('token');
      const userStr = localStorage.getItem('user');
      
      if (token && userStr) {
        try {
          const user = JSON.parse(userStr);
          state.user = user;
          state.token = token;
          state.isAuthenticated = true;
        } catch (error) {
          // Clear invalid data
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          state.user = null;
          state.token = null;
          state.isAuthenticated = false;
        }
      }
      
      // Mark as initialized and stop loading
      state.isInitialized = true;
      state.isLoading = false;
    },
  },
  extraReducers: (builder) => {
    // Listen for the logoutWithCacheClear action and clear RTK Query cache
    builder.addCase(authSlice.actions.logoutWithCacheClear, (state) => {
      // Reset the API cache when logging out
      return state;
    });
    
    // Clear cache when baseApi.util.resetApiState is dispatched
    builder.addMatcher(
      baseApi.util.resetApiState.match,
      (state) => {
        // This matcher will trigger when the API state is reset
        return state;
      }
    );
  },
});

export const {
  setCredentials,
  clearCredentials,
  logoutWithCacheClear,
  forceLogout,
  setLoading,
  setError,
  updateUser,
  initializeAuth,
} = authSlice.actions;

// Selectors
export const selectCurrentUser = (state) => state.auth.user;
export const selectCurrentToken = (state) => state.auth.token;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectAuthLoading = (state) => state.auth.isLoading;
export const selectAuthInitialized = (state) => state.auth.isInitialized;
export const selectAuthError = (state) => state.auth.error;

// Role-based selectors
export const selectIsAdmin = (state) => state.auth.user?.role === 'admin';
export const selectIsTeacher = (state) => state.auth.user?.role === 'teacher';
export const selectIsStudent = (state) => state.auth.user?.role === 'student';

export default authSlice.reducer;
